// Dev-only guided checklist for the Initial evaluation manual smoke.
// State lives in sessionStorage so leaving the Students page does not wipe the run.
// Production builds never include this module (@dev-only resolves to stubs).

import { reactive } from 'vue'

const STORAGE_KEY = 'cmv-eval-smoke-guide'

export const EVAL_SMOKE_STEPS = [
  {
    id: 'list',
    title: 'Initial list and consent gate',
    do: 'As admin/chair/psych, open Students → Initial and the student’s regular row. Compare before and after AP consent; the yellow evaluation tile appears only after consent.',
    checks: [
      { id: 'columns', label: 'The table is Student, Eval info, Schedule, Action, and Status' },
      {
        id: 'tile',
        label:
          'After consent, the student row shows the pending evaluation, eval case manager and initial meeting; before consent the yellow tile is absent'
      }
    ]
  },
  {
    id: 'dates',
    title: 'Dates and suggested due',
    do: 'Edit info. Set Referred, then AP sent, then AP consent received. Leave Due date empty if the school calendar is set.',
    checks: [
      {
        id: 'chips',
        label:
          'Dates stack as chips: Referred, AP sent, AP consent received, Due date, Initial meeting'
      },
      {
        id: 'suggest',
        label: 'After consent, Suggested due appears and the info icon explains the 60-day clock'
      },
      { id: 'next', label: 'Status says Next: for the first date that is still empty' }
    ]
  },
  {
    id: 'psych',
    title: 'Psychologists and schedule',
    do: 'In Edit info, assign two psychologists. Then use Schedule on the row.',
    checks: [
      { id: 'both', label: 'Both psychologist names show on the row' },
      { id: 'schedule', label: 'Schedule opens the schedule editor for that student' }
    ]
  },
  {
    id: 'meeting',
    title: 'Initial meeting',
    do: 'Use Meeting on the row. Save a date. Then check the Initial row and the student tile.',
    checks: [
      { id: 'type', label: 'The new meeting starts as Initial' },
      {
        id: 'psych',
        label: 'The psychologist is in their own section, not under service providers'
      },
      {
        id: 'coord',
        label: 'An unchanged school-default coordinator shows a check, not the set-default checkbox'
      },
      {
        id: 'date',
        label: 'The date shows on the Initial row and on the student tile with a yellow border'
      }
    ]
  },
  {
    id: 'feedback',
    title: 'Initial feedback',
    do: 'Use Feedback on the Initial row and send one. Separately, a plan-review feedback on a 504 must not change this button.',
    checks: [
      { id: 'type', label: 'Meeting type includes Initial and starts on Initial' },
      { id: 'date', label: 'The meeting date is the initial meeting date' },
      { id: 'button', label: 'After send, the Initial Feedback button shows sent or overdue' },
      { id: 'scoped', label: 'A plan-review feedback does not change the Initial Feedback button' }
    ]
  },
  {
    id: 'eligible',
    title: 'Eligible on a live 504',
    do: 'Use a student who already has a 504 and an open IEP initial. Record outcome → Eligible, with a case manager.',
    checks: [
      {
        id: 'write',
        label: 'The student becomes IEP with that case manager, and Initial pending clears'
      },
      { id: 'gone', label: 'The evaluation leaves the open Initial list' }
    ]
  },
  {
    id: 'shell',
    title: 'Not eligible on a shell',
    do: 'On an Initial-pending shell, Record outcome → Not eligible. Try leave, then (on a throwaway) close and remove.',
    checks: [
      {
        id: 'leave',
        label: 'Leave keeps the student as Initial pending and closes the evaluation'
      },
      { id: 'remove', label: 'Close and remove asks you to type DELETE, then the student is gone' }
    ]
  },
  {
    id: 'phone',
    title: 'Phone layout',
    do: 'Narrow the window below a phone width while the Initial list is open.',
    checks: [
      {
        id: 'cards',
        label: 'Each row becomes a card, and Meeting, Feedback, and Record outcome stay usable'
      }
    ]
  },
  {
    id: 'form',
    title: 'Student form',
    do: 'Open the student from the main list while an initial is still open.',
    checks: [
      {
        id: 'note',
        label:
          'The active Evaluation section is highlighted and Open Initial table focuses this student; the old Initial checkbox is absent'
      }
    ]
  }
]

const emptyState = () => ({
  startedAt: null,
  stepIndex: 0,
  checks: {},
  actions: []
})

export const evalSmokeState = reactive(emptyState())

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(evalSmokeState))
  } catch {
    /* private mode / quota — the in-memory run still works */
  }
}

function hydrate() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw)
    evalSmokeState.startedAt = saved.startedAt || null
    evalSmokeState.stepIndex = Number(saved.stepIndex) || 0
    evalSmokeState.checks = saved.checks && typeof saved.checks === 'object' ? saved.checks : {}
    evalSmokeState.actions = Array.isArray(saved.actions) ? saved.actions : []
  } catch {
    /* ignore a corrupt blob */
  }
}

hydrate()

export function logEvalSmoke(event, detail = {}) {
  if (!evalSmokeState.startedAt) evalSmokeState.startedAt = new Date().toISOString()
  const row = { t: new Date().toISOString(), event }
  for (const [key, value] of Object.entries(detail)) {
    if (value == null || value === '') continue
    row[key] = value
  }
  evalSmokeState.actions.push(row)
  if (evalSmokeState.actions.length > 200) {
    evalSmokeState.actions.splice(0, evalSmokeState.actions.length - 200)
  }
  applyAutoChecks()
  persist()
}

function setAutoCheck(id, status, note) {
  const prev = evalSmokeState.checks[id]
  if (prev && prev.auto === false) return
  evalSmokeState.checks[id] = { status, note, auto: true }
}

// A finished action can prove the write. What the page looks like stays for you.
export function evalAutoMarksFromActions(actions) {
  const rows = Array.isArray(actions) ? actions : []
  const marks = {}
  const set = (id, status, note) => {
    marks[id] = { status, note }
  }
  const has = (event, pred = () => true) => rows.some((row) => row.event === event && pred(row))

  if (has('schedule')) {
    set('psych.schedule', 'pass', 'From the log: Schedule opened the editor for that student.')
  }
  if (has('outcome-saved', (row) => row.outcome === 'eligible')) {
    const plan = rows.find(
      (row) => row.event === 'outcome-saved' && row.outcome === 'eligible'
    )?.plan
    set(
      'eligible.write',
      'pass',
      `From the log: eligibility was applied${plan ? ` as ${plan}` : ''}. Initial pending clears with that save.`
    )
    set(
      'eligible.gone',
      'pass',
      'From the log: the evaluation was closed, so it leaves the open Initial list.'
    )
  }
  if (
    has(
      'outcome-saved',
      (row) =>
        (row.outcome === 'notEligible' || row.outcome === 'withdrawn') &&
        String(row.removed) !== '1'
    )
  ) {
    set(
      'shell.leave',
      'pass',
      'From the log: the evaluation was closed and the student was left in place.'
    )
  }
  if (has('outcome-saved', (row) => String(row.removed) === '1')) {
    set('shell.remove', 'pass', 'From the log: Close and remove deleted the student.')
  }
  return marks
}

function applyAutoChecks() {
  const marks = evalAutoMarksFromActions(evalSmokeState.actions)
  for (const [id, mark] of Object.entries(marks)) {
    setAutoCheck(id, mark.status, mark.note)
  }
}

export function setEvalSmokeCheck(id, status, note) {
  const prev = evalSmokeState.checks[id] || {}
  evalSmokeState.checks[id] = {
    status: status == null ? prev.status || '' : status,
    note: note != null ? note : prev.note || '',
    auto: false
  }
  persist()
}

export function setEvalSmokeStep(index) {
  const max = EVAL_SMOKE_STEPS.length - 1
  evalSmokeState.stepIndex = Math.min(max, Math.max(0, index))
  persist()
}

export function resetEvalSmoke() {
  const fresh = emptyState()
  evalSmokeState.startedAt = fresh.startedAt
  evalSmokeState.stepIndex = 0
  evalSmokeState.checks = {}
  evalSmokeState.actions = []
  persist()
}

function allChecks() {
  const rows = []
  for (const step of EVAL_SMOKE_STEPS) {
    for (const check of step.checks) {
      const saved = evalSmokeState.checks[`${step.id}.${check.id}`] || {}
      rows.push({
        step: step.title,
        label: check.label,
        status: saved.status || 'unmarked',
        note: saved.note || ''
      })
    }
  }
  return rows
}

export function evalSmokeReport() {
  const checks = allChecks()
  const counts = { pass: 0, fail: 0, skip: 0, unmarked: 0 }
  for (const row of checks) counts[row.status] = (counts[row.status] || 0) + 1
  return {
    kind: 'initial-eval-manual-smoke',
    startedAt: evalSmokeState.startedAt,
    copiedAt: new Date().toISOString(),
    counts,
    checks,
    actions: evalSmokeState.actions.slice()
  }
}

export function evalSmokeMarkdown() {
  const report = evalSmokeReport()
  const lines = [
    'Initial eval smoke',
    `started: ${report.startedAt || '—'}`,
    `pass ${report.counts.pass} · fail ${report.counts.fail} · skip ${report.counts.skip} · unmarked ${report.counts.unmarked}`,
    ''
  ]
  let lastStep = ''
  for (const row of report.checks) {
    if (row.step !== lastStep) {
      lines.push(row.step)
      lastStep = row.step
    }
    const mark = row.status === 'pass' ? 'pass' : row.status === 'fail' ? 'FAIL' : row.status
    lines.push(`- ${mark}: ${row.label}${row.note ? ` — ${row.note}` : ''}`)
  }
  lines.push('', 'Actions')
  const recent = report.actions.slice(-40)
  if (!recent.length) lines.push('(none yet)')
  for (const action of recent) {
    const extra = Object.entries(action)
      .filter(([key]) => key !== 't' && key !== 'event')
      .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join(', ') : value}`)
      .join(' ')
    lines.push(`${action.t.slice(11, 19)} ${action.event}${extra ? ` ${extra}` : ''}`)
  }
  return lines.join('\n')
}
